import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { OrderApi } from 'api';
import type { Order } from 'api/methods/OrderApi';

/**
 * [Order] Query key 생성
 * --
 */
export const orderQueryKeys = createQueryKeys('order', {
  getOrder: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getOrders: (params?: { [key: string]: string | number }) => [params],
  getGuestOrder: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [Order] 주문 전체 조회
 * --
 */
export const useGetOrders = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: orderQueryKeys.getOrders(params).queryKey,
    queryFn: () => OrderApi.getOrders(params),
    ...options,
  });
  return data;
};

/**
 * [Order] 주문 상세 조회
 * --
 */
export const useGetOrder = (
  idx: number,
  params?: { [key: string]: string | number },
  options?: { [key: string]: Record<string, any> }
) => {
  const data = useQuery({
    queryKey: orderQueryKeys.getOrder(idx, params).queryKey,
    queryFn: () => OrderApi.getOrder(idx, params),
    ...options,
  });

  return data;
};

/**
 * [Order] 주문 생성
 * --
 */
export const useCreateOrder = (body: Order) => {
  const { mutateAsync: createOrder, ...result } = useMutation({
    mutationFn: (variables: Order) => OrderApi.createOrder(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createOrder, ...result };
};

/**
 * [Order] 주문 수정
 * --
 */
export const useUpdateOrder = (body: Order) => {
  const { mutateAsync: updateOrder, ...result } = useMutation({
    mutationFn: (variables: Order) => OrderApi.updateOrder(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateOrder, ...result };
};

/**
 * [Order] 주문 상태 수정
 * --
 */
export const useUpdateStatusOrder = (body: Order) => {
  const { mutateAsync: updateStatusOrder, ...result } = useMutation({
    mutationFn: (variables: Order) => OrderApi.updateStatusOrder(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateStatusOrder, ...result };
};

/**
 * [Order] 주문 삭제
 * --
 */
export const useDeleteOrder = (idx: number) => {
  const { mutateAsync: deleteOrder, ...result } = useMutation({
    mutationFn: (variables: number) => OrderApi.deleteOrder(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteOrder, ...result };
};

/**
 * [Order] 비회원 주문 조회
 * --
 */
export const useGetGuestOrder = (
  params?: {
    [key: string]: string | number;
  },
  options?: { [key: string]: Record<string, any> }
) => {
  const data = useQuery({
    queryKey: orderQueryKeys.getGuestOrder(params).queryKey,
    queryFn: () => OrderApi.getGuestOrder(params),
    ...options,
  });
  return data;
};

/**
 * [Order] 수동 주문 생성
 * --
 */
export const useManualCreateOrder = () => {
  const { mutateAsync: createManualOrder, ...result } = useMutation({
    mutationFn: (variables: Order) => OrderApi.createManualOrder(variables),
  });

  return { createManualOrder, ...result };
};

/**
 * [Order] 주문 생성/수정
 * --
 */
export const useUpsertOrder = () => {
  const { mutateAsync: upsertOrder, ...result } = useMutation({
    mutationFn: (variables: Order[]) => OrderApi.upsertOrder(variables),
  });

  return { upsertOrder, ...result };
};
